import { getStoredAuthTokenState } from '../auth/storage';
import { emitSessionExpiredOnce } from '../auth/sessionEvents';
import { emitLegalAcceptanceRequired } from '../auth/legalGateSignal';
import { applyAdminWriteTokenHeader } from '../pages/admin/adminWriteToken';
import { nativeRequestInfo } from '../native/requestInfo';
import { API_CREDENTIALS } from './http-credentials';

export interface ApiErrorShape {
  error_code?: string;
  request_id?: string;
  retryable?: boolean;
  status: number;
  data: unknown;
  code: string | undefined;
  hadBearerAuthorization: boolean;
}

export class ApiError extends Error implements ApiErrorShape {
  status: number;
  data: unknown;
  code: string | undefined;
  hadBearerAuthorization: boolean;
  error_code?: string;
  request_id?: string;
  retryable?: boolean;

  constructor(status: number, data: unknown, hadBearerAuthorization = false) {
    super(`API request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    const errorCode = data && typeof data === 'object'
      ? (data as { error_code?: unknown; code?: unknown }).error_code ?? (data as { code?: unknown }).code
      : undefined;
    this.code = typeof errorCode === 'string' && errorCode.length > 0 ? errorCode : undefined;
    this.error_code = this.code;
    const details = data && typeof data === 'object' ? data as Record<string, unknown> : {};
    this.request_id = typeof details.request_id === 'string' ? details.request_id : undefined;
    this.retryable = typeof details.retryable === 'boolean' ? details.retryable : undefined;
    this.hadBearerAuthorization = hadBearerAuthorization;
    if (hadBearerAuthorization && this.code === 'session_expired') {
      emitSessionExpiredOnce();
    }
    // Server sperrt die Nutzung bis zur Zustimmung (createLegalUsageGuard):
    // das Gate holt /legal-status neu, falls es sich fuer erledigt haelt.
    if (status === 409 && this.code === 'legal_acceptance_required') {
      emitLegalAcceptanceRequired();
    }
  }
}

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  // Warum: optionaler Timeout, damit Aufrufer bei Bedarf (z.B. lange Bootstrap-
  // Folgen) einen anderen Wert setzen können. Default siehe DEFAULT_API_TIMEOUT_MS.
  timeoutMs?: number;
}

const API_BASE = import.meta.env.VITE_API_BASE ?? '';
export const MEMBERSHIP_REQUIRED_EVENT = 'lorath:membership-required';

function emitMembershipRequired(data: unknown): void {
  if (typeof window === 'undefined' || typeof CustomEvent === 'undefined') return;
  if (!data || typeof data !== 'object') return;
  const payload = data as { error?: unknown; tier?: unknown };
  if (payload.error !== 'membership_required') return;
  const tier = payload.tier === 'ultra' ? 'ultra' : 'plus';
  window.dispatchEvent(new CustomEvent(MEMBERSHIP_REQUIRED_EVENT, { detail: { tier } }));
}

// Warum: gegen hängende Requests (Android-Problem "Deine Inhalte werden eingerichtet").
// 25s reicht für langsame Mobilfunk-Roundtrips, killt aber echte Hänger.
export const DEFAULT_API_TIMEOUT_MS = 25_000;

function isBodyInit(value: unknown): value is BodyInit {
  return value instanceof FormData
    || value instanceof Blob
    || value instanceof URLSearchParams
    || value instanceof ArrayBuffer
    || ArrayBuffer.isView(value)
    || typeof value === 'string';
}

function toUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE}${path}`;
}

function normalizeBearerToken(token: string | null): string | null {
  const trimmed = token?.trim();
  if (!trimmed || /[\r\n]/.test(trimmed)) return null;
  return trimmed;
}

export interface StoredAuthorizationResult {
  isNative: boolean;
  hadBearerAuthorization: boolean;
}

export async function applyStoredAuthorizationHeader(headers: Headers): Promise<StoredAuthorizationResult> {
  const stored = await getStoredAuthTokenState();
  const token = normalizeBearerToken(stored.token);
  if (!token) {
    return {
      isNative: stored.isNative,
      hadBearerAuthorization: /^Bearer\s+\S+/i.test(headers.get('Authorization') ?? ''),
    };
  }

  try {
    headers.set('Authorization', `Bearer ${token}`);
  } catch {
    // Invalid native preference values should fall back to the cookie/session path.
  }
  return {
    isNative: stored.isNative,
    hadBearerAuthorization: /^Bearer\s+\S+/i.test(headers.get('Authorization') ?? ''),
  };
}

async function parseResponse(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) return response.json();
  const text = await response.text();
  return text || null;
}

export interface TimeoutAbort {
  signal: AbortSignal;
  dispose: () => void;
}

// Warum: kombiniert unseren Timeout-Abbruch mit einem evtl. vom Aufrufer
// übergebenen Signal. Wichtig: das externe Signal darf den Request nicht
// stillschweigend "weiterschleifen" lassen, wenn wir selbst abbrechen.
export function createTimeoutAbort(
  externalSignal: AbortSignal | null | undefined,
  timeoutMs: number,
): TimeoutAbort {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  const onExternalAbort = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', onExternalAbort);
    }
  }

  return {
    signal: controller.signal,
    dispose: () => {
      clearTimeout(timeoutId);
      if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort);
    },
  };
}

function isTimeoutAbort(error: unknown, abort: TimeoutAbort, externalSignal: AbortSignal | null | undefined): boolean {
  if (!error || typeof error !== 'object' || !('name' in error) || error.name !== 'AbortError') return false;
  if (!abort.signal.aborted) return false;
  // Warum: wenn der Aufrufer selbst abgebrochen hat, ist es kein Timeout.
  if (externalSignal?.aborted) return false;
  return true;
}

let nativeHeaderCapability: { expires: number; promise: Promise<boolean> } | undefined;
async function allowsNativeRequestHeaders(): Promise<boolean> {
  if (!nativeHeaderCapability || nativeHeaderCapability.expires <= Date.now()) {
    // Der Probe-Request muss auch vor dem Server-Deploy ohne neue Preflight-Header funktionieren.
    const promise = (async () => {
      const abort = createTimeoutAbort(undefined, 2000);
      try {
        const response = await fetch(toUrl('/api/readyz'), { signal: abort.signal, credentials: API_CREDENTIALS });
        if (!response.ok) return false;
        const data = await response.json() as { capabilities?: { native_request_headers?: boolean } };
        return data?.capabilities?.native_request_headers === true;
      } catch { return false; }
      finally { abort.dispose(); }
    })();
    nativeHeaderCapability = { expires: Date.now() + 60_000, promise };
  }
  return nativeHeaderCapability.promise;
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  const authorization = await applyStoredAuthorizationHeader(headers);
  applyAdminWriteTokenHeader(headers, path);

  // Native Requests haben keinen Cookie-Fallback. Ohne gespeicherten Token ist
  // /me deshalb sicher ein Guest-Boot; der unautorisierte Request wuerde nur
  // einen irrefuehrenden 401 erzeugen und durfte frueher sogar den Datensatz-Wipe
  // anstossen. Web bleibt unveraendert cookie-basiert.
  if (authorization.isNative
      && !authorization.hadBearerAuthorization
      && path.split('?')[0] === '/api/auth/me') {
    throw new ApiError(401, { code: 'native_auth_token_missing' }, false);
  }

  const nativeInfo = await nativeRequestInfo();
  if (Object.keys(nativeInfo).length && await allowsNativeRequestHeaders()) {
    for (const [key, value] of Object.entries(nativeInfo)) headers.set(key, value);
  }

  let body: BodyInit | null | undefined;
  if (options.body !== undefined) {
    if (isBodyInit(options.body)) {
      body = options.body;
    } else {
      headers.set('Content-Type', headers.get('Content-Type') ?? 'application/json');
      body = JSON.stringify(options.body);
    }
  }

  const abort = createTimeoutAbort(options.signal, options.timeoutMs ?? DEFAULT_API_TIMEOUT_MS);

  try {
    const response = await fetch(toUrl(path), {
      ...options,
      signal: abort.signal,
      headers,
      body,
      credentials: API_CREDENTIALS,
    });

    const data = await parseResponse(response);
    if (!response.ok) {
      if (response.status === 403) emitMembershipRequired(data);
      throw new ApiError(response.status, data, authorization.hadBearerAuthorization);
    }
    return data as T;
  } catch (error) {
    // Warum: Timeout/Abbruch als klaren API-Fehler weitergeben, damit die
    // catch-Blöcke der SyncEngine sauber auf 'error' umschalten können,
    // statt ewig im "bootstrapping"-Zustand zu hängen.
    if (error instanceof ApiError) throw error;
    if (isTimeoutAbort(error, abort, options.signal)) {
      throw new ApiError(0, { code: 'request_timeout' });
    }
    throw error;
  } finally {
    abort.dispose();
  }
}
